import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import { FieldArrayRenderProps } from 'formik/dist/FieldArray'
import { InvoiceProps } from '../InvoiceForm'
import { Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components/unstable'
import { Stack, StackItem } from '@fluentui/react'
import { Badge, Button, CounterBadge, Text, Tooltip } from '@fluentui/react-components'
import {
    AddCircleRegular,
    DeleteRegular,
    DismissCircleRegular,
    EditRegular,
    ReceiptMoneyRegular,
    ShiftsRegular,
    TaskListLtrRegular,
} from '@fluentui/react-icons'
import MoneyText from '../../../../Text/Currency/MoneyText'
import {
    InvoiceMutationDto,
    LineItemGroupMutationDto,
    LineItemMutationDto,
    LineItemType,
} from '../../../../../config/finance/invoiceConfig'
import LineItemGroupDialog from './Dialog/LineItemGroupDialog'
import { FieldArray, useFormikContext } from 'formik'
import useCurrency from '../../../../../hooks/useCurrency'
import { PricePrecedence } from '../../../../../config/finance/financeConfig'
import { Link } from 'react-router-dom'
import LineItemDialog from '../LineItem/LineItemDialog'
import AssignedLineItem from '../LineItem/AssignedLineItem'
import DimensionFields, { DimensionFieldTuple } from '../../../Accounting/DimensionFields'
import useUnits from '../../../../../hooks/useUnits'
import { useTranslation } from 'react-i18next'
import TimeText from '../../../../Text/Time/TimeText'
import DistanceText from '../../../../Text/Distance/DistanceText'
import { VehicleCarProfileRegular } from '@fluentui/react-icons'

export type LineItemGroupProps = FieldArrayRenderProps &
    InvoiceProps & {
        lineItemGroup: LineItemGroupMutationDto // Extracted from Formik state.
        index: number
        prefix: string // Formik field name of the line item group.
        isDragging: boolean
        setCurrentLineItem: (lineItem: LineItemMutationDto) => void
    }

function Actions(props: LineItemGroupProps & { setEdit: (edit: boolean) => void }): ReactElement {
    const { t: translate } = useTranslation()

    return (
        <Stack horizontal verticalAlign="center">
            <StackItem>
                <Tooltip
                    content={translate('invoice.edit.form.invoicegroup.edit.tooltip')}
                    relationship="description"
                    withArrow
                >
                    <Button
                        appearance="transparent"
                        icon={<EditRegular />}
                        onClick={event => {
                            event.stopPropagation()
                            props.setEdit(true)
                        }}
                    />
                </Tooltip>
            </StackItem>
            <StackItem>
                <Tooltip
                    content={translate('invoice.edit.form.invoicegroup.delete.tooltip')}
                    relationship="description"
                    withArrow
                >
                    <Button
                        appearance="transparent"
                        icon={<DeleteRegular />}
                        onClick={() => props.remove(props.index)}
                    />
                </Tooltip>
            </StackItem>
        </Stack>
    )
}

function Aside(props: LineItemGroupProps): ReactElement {
    const { lineItemGroup } = props
    const { lineItems } = lineItemGroup
    const { values } = useFormikContext<InvoiceMutationDto>()
    const { currency } = useCurrency(values.currency)
    const price = props.lineItemGroupPrices.get(lineItemGroup.id)

    return (
        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 's1' }}>
            {lineItems?.filter(item => item.type === LineItemType.Time)?.length > 0 && (
                <Stack>
                    <Badge size="medium" icon={<ShiftsRegular />} appearance="tint">
                        <TimeText value={props.lineItemEntities.getTotalTime(lineItems)} />
                    </Badge>
                </Stack>
            )}
            {lineItems?.filter(item => item.type === LineItemType.Travel)?.length > 0 && (
                <Stack>
                    <Badge size="medium" icon={<VehicleCarProfileRegular />} appearance="tint">
                        <DistanceText value={props.lineItemEntities.getTotalDistance(lineItems)} />
                    </Badge>
                </Stack>
            )}
            {lineItemGroup.showStatement && lineItems.length > 0 && (
                <Stack>
                    <TaskListLtrRegular />
                </Stack>
            )}
            {lineItemGroup.precedence !== PricePrecedence.Inherited && lineItems.length > 0 && (
                <Stack>
                    <ReceiptMoneyRegular />
                </Stack>
            )}
            <StackItem>
                <MoneyText amount={price.subtotal} currency={currency} textProps={{ size: 400 }} />
            </StackItem>
        </Stack>
    )
}

function Description({ description }: { description?: string }): ReactElement {
    const { t: translate } = useTranslation()
    return (
        <Text size={500} italic={!description}>
            {description || translate('invoice.edit.form.invoicegroup.empty.description')}
        </Text>
    )
}

function LineItemGroupContent(props: LineItemGroupProps & { setEdit: (edit: boolean) => void }): ReactElement {
    const { t: translate } = useTranslation()

    const { values } = useFormikContext<InvoiceMutationDto>()
    const { units } = useUnits()
    const unit = useMemo(() => (units || []).find(unit => unit.key === values.unit), [units, values.unit])
    const accounting = props.lineItemGroup.accounting

    return (
        <Stack horizontal grow>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 'm' }} grow>
                {props.isEditable ? (
                    <Link
                        to="#"
                        onClick={event => {
                            event.preventDefault()
                            props.setEdit(true)
                        }}
                    >
                        <Description description={props.lineItemGroup.description} />
                    </Link>
                ) : (
                    <Description description={props.lineItemGroup.description} />
                )}

                {accounting && unit && (
                    <Stack horizontal tokens={{ childrenGap: 's2' }}>
                        <DimensionFields unit={unit}>
                            {([dimension, property]: DimensionFieldTuple) =>
                                accounting[property] && (
                                    <Tooltip
                                        relationship="description"
                                        content={translate(
                                            `accounting.dimensions.entity.label.other.${dimension.toString().toLowerCase().replaceAll(/[-_]/g, '')}`,
                                        )}
                                        withArrow
                                        key={property}
                                    >
                                        <Badge size="medium" appearance="tint">
                                            {accounting[property].code}
                                        </Badge>
                                    </Tooltip>
                                )
                            }
                        </DimensionFields>
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}

export default function LineItemGroup(props: LineItemGroupProps): ReactElement {
    const { t: translate } = useTranslation()

    const { lineItemGroup, prefix, lineItemEntities } = props
    const { lineItems } = lineItemGroup
    const { setFieldValue } = useFormikContext<InvoiceMutationDto>()
    const [edit, setEdit] = useState(false)
    const [addLineItems, setAddLineItems] = useState(false)

    return (
        <>
            <TreeItem
                itemType={!props.isEditable && lineItems.length === 0 ? 'leaf' : 'branch'}
                onClick={event => edit && event.preventDefault()}
            >
                <TreeItemLayout
                    iconBefore={<CounterBadge size="medium" count={lineItems.length} showZero />}
                    actions={props.isEditable ? <Actions {...props} setEdit={setEdit} /> : undefined}
                    aside={<Aside {...props} />}
                >
                    <LineItemGroupContent {...props} setEdit={setEdit} />
                </TreeItemLayout>
                <Tree>
                    <Stack tokens={{ childrenGap: 's2' }} grow>
                        <FieldArray name={`${prefix}.lineItems`}>
                            {arrayHelpers => (
                                <Stack>
                                    {lineItems.map((lineItem: LineItemMutationDto, index: number) => (
                                        <AssignedLineItem
                                            key={lineItem.entityId}
                                            {...props}
                                            {...arrayHelpers}
                                            lineItem={lineItem}
                                            index={index}
                                        />
                                    ))}
                                </Stack>
                            )}
                        </FieldArray>
                        {props.isEditable && (
                            <Stack horizontalAlign="center" horizontal>
                                <Button
                                    disabled={!lineItemEntities.hasOrphans}
                                    size="small"
                                    appearance="transparent"
                                    icon={<AddCircleRegular />}
                                    onClick={() => setAddLineItems(true)}
                                >
                                    {translate('invoice.edit.form.invoiceline.add')}
                                </Button>
                                {lineItems.length > 0 && (
                                    <Button
                                        size="small"
                                        appearance="transparent"
                                        icon={<DismissCircleRegular />}
                                        onClick={() => setFieldValue(`${prefix}.lineItems`, [])}
                                    >
                                        {translate('invoice.edit.form.invoiceline.clear')}
                                    </Button>
                                )}
                            </Stack>
                        )}
                    </Stack>
                </Tree>
            </TreeItem>
            {edit && props.isEditable && (
                <LineItemGroupDialog
                    {...props}
                    key={`dialog-group-${lineItemGroup.id}`}
                    open={edit}
                    setOpen={setEdit}
                    isNew={false}
                />
            )}
            <LineItemDialog {...props} open={addLineItems} setOpen={setAddLineItems} />
        </>
    )
}
